import { useMemo, useRef, useState } from "react";

import { Button } from "../../components/Button";

/** Родитель для группировки: верхний уровень — ключ 0. */
function categoryParentKey(cat) {
  const p = cat.parent_id;
  if (p == null || p === "") return 0;
  return Number(p);
}

function buildChildrenByParent(categories) {
  const m = new Map();
  for (const c of categories) {
    const k = categoryParentKey(c);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(c);
  }
  for (const arr of m.values()) {
    arr.sort((a, b) => (a.sort_order - b.sort_order) || (a.id - b.id));
  }
  return m;
}

/** Все id категории rootId и её потомков (любая глубина). */
function collectDescendantCategoryIds(rootId, categories) {
  const byParent = buildChildrenByParent(categories);
  const out = new Set();
  const walk = (id) => {
    const n = Number(id);
    out.add(n);
    for (const ch of byParent.get(n) || []) walk(ch.id);
  };
  walk(rootId);
  return out;
}

export function AdminMenuSection({
  loading,
  categoryDraft,
  setCategoryDraft,
  onCategorySubmit,
  editingCategoryId,
  startEditCategory,
  cancelEditCategory,
  menuCategories,
  removeMenuCategory,
  itemDraft,
  setItemDraft,
  onMenuItemSubmit,
  editingMenuItemId,
  startEditMenuItem,
  cancelEditMenuItem,
  menuItems,
  toggleMenuItem,
  removeMenuItem,
}) {
  const [filterSectionId, setFilterSectionId] = useState("");
  const [filterSubId, setFilterSubId] = useState("");
  const categoryFormRef = useRef(null);
  const menuItemFormRef = useRef(null);

  const childrenByParent = useMemo(
    () => buildChildrenByParent(menuCategories),
    [menuCategories],
  );

  const topLevelCategories = useMemo(
    () => childrenByParent.get(0) || [],
    [childrenByParent],
  );

  const subcategories = useMemo(() => {
    if (!filterSectionId) return [];
    return childrenByParent.get(Number(filterSectionId)) || [];
  }, [childrenByParent, filterSectionId]);

  const filteredMenuItems = useMemo(() => {
    if (!filterSectionId) return menuItems;
    const rootId = filterSubId ? Number(filterSubId) : Number(filterSectionId);
    const allowed = collectDescendantCategoryIds(rootId, menuCategories);
    return menuItems.filter((item) => allowed.has(Number(item.category_id)));
  }, [menuItems, menuCategories, filterSectionId, filterSubId]);

  const onFilterSectionChange = (e) => {
    setFilterSectionId(e.target.value);
    setFilterSubId("");
  };

  const scrollToForm = (formRef) => {
    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleStartEditCategory = (cat) => {
    startEditCategory(cat);
    scrollToForm(categoryFormRef);
  };

  const handleStartEditMenuItem = (item) => {
    startEditMenuItem(item);
    scrollToForm(menuItemFormRef);
  };

  return (
    <>
      <form className="adminForm" onSubmit={onCategorySubmit} ref={categoryFormRef}>
        <h3>{editingCategoryId ? "Редактировать уровень / подуровень" : "Добавить уровень / подуровень"}</h3>
        <label className="field">
          Родитель
          <select
            value={categoryDraft.parent_id}
            onChange={(e) => setCategoryDraft((prev) => ({ ...prev, parent_id: e.target.value }))}
          >
            <option value="">Без родителя (верхний уровень)</option>
            {menuCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Название
          <input
            type="text"
            value={categoryDraft.title}
            onChange={(e) => setCategoryDraft((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Например, Улуны"
          />
        </label>
        <label className="field">
          Порядок сортировки
          <input
            type="number"
            min="0"
            value={categoryDraft.sort_order}
            onChange={(e) => setCategoryDraft((prev) => ({ ...prev, sort_order: e.target.value }))}
          />
        </label>
        <p className="muted adminMenuFormHint">
          У верхних разделов — порядок среди разделов. У подкатегорий — порядок среди «соседей» с тем же
          родителем (например, все сорта пуэра внутри «Пуэр»).
        </p>
        <Button type="submit" variant="accent">
          {editingCategoryId ? "Сохранить уровень" : "Добавить уровень"}
        </Button>
        {editingCategoryId ? (
          <Button type="button" onClick={cancelEditCategory}>
            Отменить редактирование
          </Button>
        ) : null}
      </form>

      <section className="adminGroup">
        <h3>Уровни меню</h3>
        {loading ? <p className="muted">Загрузка...</p> : null}
        {!loading && menuCategories.length === 0 ? <p className="muted">Пока нет уровней.</p> : null}
        {!loading
          ? menuCategories.map((cat) => (
              <article key={cat.id} className="adminItem">
                <div>
                  <h4>{cat.title}</h4>
                  <p className="muted">
                    Порядок: {cat.sort_order ?? 0}
                    {cat.parent_id != null
                      ? ` · подраздел (родитель id ${cat.parent_id})`
                      : " · верхний уровень"}
                  </p>
                </div>
                <div className="adminItemActions">
                  <Button
                    type="button"
                    className={editingCategoryId === cat.id ? "adminEditButton is-active" : "adminEditButton"}
                    aria-pressed={editingCategoryId === cat.id}
                    onClick={() => handleStartEditCategory(cat)}
                  >
                    Править
                  </Button>
                  <Button type="button" onClick={() => removeMenuCategory(cat)}>
                    Удалить
                  </Button>
                </div>
              </article>
            ))
          : null}
      </section>

      <form className="adminForm" onSubmit={onMenuItemSubmit} ref={menuItemFormRef}>
        <h3>{editingMenuItemId ? "Редактировать лот" : "Добавить лот"}</h3>
        <label className="field">
          Категория / подкатегория
          <select
            value={itemDraft.category_id}
            onChange={(e) => setItemDraft((prev) => ({ ...prev, category_id: e.target.value }))}
          >
            <option value="">Выбери уровень</option>
            {menuCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Название лота
          <input
            type="text"
            value={itemDraft.title}
            onChange={(e) => setItemDraft((prev) => ({ ...prev, title: e.target.value }))}
          />
        </label>
        <label className="field">
          Описание
          <textarea
            rows="3"
            value={itemDraft.description}
            onChange={(e) => setItemDraft((prev) => ({ ...prev, description: e.target.value }))}
          />
        </label>
        <label className="field">
          Цена (руб)
          <input
            type="number"
            min="0"
            value={itemDraft.price_rub}
            onChange={(e) => setItemDraft((prev) => ({ ...prev, price_rub: e.target.value }))}
          />
        </label>
        <label className="field">
          Порядок в категории
          <input
            type="number"
            min="0"
            value={itemDraft.sort_order}
            onChange={(e) => setItemDraft((prev) => ({ ...prev, sort_order: e.target.value }))}
          />
        </label>
        <p className="muted adminMenuFormHint">
          Меньшее число — выше в списке позиций внутри выбранной категории (на экране меню и в админке).
        </p>
        <Button type="submit" variant="accent">
          {editingMenuItemId ? "Сохранить лот" : "Добавить лот"}
        </Button>
        {editingMenuItemId ? (
          <Button type="button" onClick={cancelEditMenuItem}>
            Отменить редактирование
          </Button>
        ) : null}
      </form>

      <section className="adminGroup">
        <h3>Лоты меню</h3>
        <div className="adminMenuItemFilter">
          <label className="field">
            Фильтр: раздел
            <select value={filterSectionId} onChange={onFilterSectionChange}>
              <option value="">Все лоты</option>
              {topLevelCategories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.title}
                </option>
              ))}
            </select>
          </label>
          {filterSectionId && subcategories.length > 0 ? (
            <label className="field">
              Подраздел
              <select value={filterSubId} onChange={(e) => setFilterSubId(e.target.value)}>
                <option value="">Все в этом разделе</option>
                {subcategories.map((cat) => (
                  <option key={cat.id} value={String(cat.id)}>
                    {cat.title}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {filterSectionId ? (
          <p className="muted adminMenuItemFilterHint">
            Показано: {filteredMenuItems.length} из {menuItems.length}
          </p>
        ) : null}
        {loading ? <p className="muted">Загрузка...</p> : null}
        {!loading && menuItems.length === 0 ? <p className="muted">Пока нет лотов.</p> : null}
        {!loading && menuItems.length > 0 && filteredMenuItems.length === 0 ? (
          <p className="muted">В выбранном разделе нет лотов.</p>
        ) : null}
        {!loading
          ? filteredMenuItems.map((item) => (
              <article key={item.id} className="adminItem">
                <div>
                  <h4>{item.title}</h4>
                  <p className="muted">Категория: {item.category_title || item.category_id}</p>
                  <p className="muted">Порядок в категории: {item.sort_order ?? 0}</p>
                  <p>{item.description || "—"}</p>
                  <p>Цена: {item.price_rub} руб.</p>
                  <p className="muted">Статус: {item.is_active ? "активен" : "скрыт"}</p>
                </div>
                <div className="adminItemActions">
                  <Button type="button" onClick={() => toggleMenuItem(item)}>
                    {item.is_active ? "Скрыть" : "Показать"}
                  </Button>
                  <Button
                    type="button"
                    className={editingMenuItemId === item.id ? "adminEditButton is-active" : "adminEditButton"}
                    aria-pressed={editingMenuItemId === item.id}
                    onClick={() => handleStartEditMenuItem(item)}
                  >
                    Править
                  </Button>
                  <Button type="button" onClick={() => removeMenuItem(item)}>
                    Удалить
                  </Button>
                </div>
              </article>
            ))
          : null}
      </section>
    </>
  );
}
